import { Modal } from 'antd';
import { onSnapshot } from 'firebase/firestore';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CheckMark, DoubleCheckMark } from '../../../components/UI/Icon-SVG/CheckMark';
import CopyIcon from '../../../components/UI/Icon-SVG/CopyIcon';
import DeleteIcon from '../../../components/UI/Icon-SVG/DeleteIcon';
import ReplyIcon from '../../../components/UI/Icon-SVG/ReplyIcon';
import SupportLargeIcon from '../../../components/UI/Icon-SVG/SupportLargeIcon';
import Spinner from '../../../components/UI/Spinner/Spinner';
import { Context } from '../../../index';
import useUsername from '../hooks/useUsername';

import AudioPlayer from './AudioPlayer';
import ChatListContextMenu, { ChatListContextMenuItem } from './ChatListContextMenu';

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  min-height: calc(100vh - 64px - 84px);
  background-image: url('/images/message-list-bg.png');
  background-color: #dbe6f2;
  padding: 0 16px;
  overflow: auto;
`;

const MessageContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin: 6px 0;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #d8ddec;
  border-radius: 20px;
  padding: 8px 8px 6px 8px;
  max-width: 380px;
  min-width: 58px;
  overflow: hidden;
`;

const UserName = styled.p`
  color: #009571;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Open Sans', sans-serif;
  margin: 0;
`;

const MessageBody = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  overflow-wrap: anywhere;
  margin: 0;
  word-break: break-all;
`;

const DateWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const Date = styled.div`
  color: #a6b0cf;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
`;

const SupportChatContainer = styled.div`
  height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  margin: 50px 0;
`;

const SupportChatTitle = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  margin-bottom: 6px;
  font-size: 18px;
`;

const SupportChatShadow = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #a6b0cf;
`;

const ImageContainer = styled.div`
  overflow: hidden;
  align-self: center;
`;

const Image = styled.img`
  height: 172px;
  width: auto;
  max-width: 600px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
`;

const CheckMarkWrapper = styled.div`
  margin-top: 1px;
  margin-left: 2px;
`;

const ReplyMessage = styled.div`
  width: 100%;
  height: 44px;
  border-left: 2px solid #397cf3;
  margin-top: 6px;
  padding-left: 8px;
  cursor: pointer;
`;

const ReplyMessageUserName = styled.div`
  font-size: 16px;
  color: #009571;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: -4px;
`;

const ReplyMessageText = styled.div`
  font-size: 16px;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  white-space: nowrap;
`;

const MessageListBody = ({
  activeChat,
  userId,
  isClientSupportChat = false,
  showGallery,
  setReplyMessage,
}) => {
  const { firestore } = useContext(Context);
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [contextMenuIsVisible, setContextMenuIsVisible] = useState(false);
  const [selectedForContextmenu, setSelectedForContextmenu] = useState(null);
  const [anchorPoint, setAnchorPoint] = useState(null);
  const [deleteConfirmModalIsVisible, setDeleteConfirmModalIsVisible] = useState(false);
  const container = useRef(null);
  const username = useUsername(userId);

  const hideDeleteModal = () => setDeleteConfirmModalIsVisible(false);
  const showDeleteModal = () => setDeleteConfirmModalIsVisible(true);
  const deleteMessage = () => {
    firestore
      .collection('chat_rooms')
      .doc(activeChat)
      .collection('chat_messages')
      .doc(selectedForContextmenu.id)
      .delete();
    hideDeleteModal();
  };

  const readMessages = (snapshot) => {
    const messages = snapshot.docs.map((item) => ({
      data: item.data(),
      id: item.id,
    }));
    const newMessages = messages.filter(
      (item) => item.data.status === 0 && item.data.sender_id !== userId,
    );
    const collectionRef = firestore
      .collection('chat_rooms')
      .doc(activeChat)
      .collection('chat_messages');
    const batch = firestore.batch();
    newMessages.forEach((item) => {
      const newDocRef = collectionRef.doc(item.id);
      batch.set(newDocRef, {
        ...item.data,
        status: 1,
      });
    });
    batch.commit();
  };

  useEffect(() => {
    let unsub;
    if (activeChat) {
      setIsLoading(true);
      unsub = onSnapshot(
        firestore.collection('chat_rooms').doc(activeChat).collection('chat_messages'),
        (snapshot) => {
          const result =
            snapshot.docs.map((item) => ({
              ...item.data(),
              id: item.id,
            })) || [];
          const sorted = result
            .filter((item) => !!item?.msg_epoch?.toDate)
            .sort(
              (a, b) =>
                // eslint-disable-next-line no-unsafe-optional-chaining
                a?.msg_epoch?.toDate() - b?.msg_epoch?.toDate(),
            );
          readMessages(snapshot);
          setChatMessages(sorted);
          setIsLoading(false);
        },
      );
    }
    return () => {
      unsub?.();
    };
  }, [activeChat]);

  const scrollToBottom = () => {
    if (container.current) {
      container.current.scrollTop = container.current.scrollHeight;
    }
  };

  const hideMenu = () => {
    setContextMenuIsVisible(false);
  };

  useEffect(() => {
    setTimeout(scrollToBottom, 300);
  }, [chatMessages?.length]);

  const copyText = () => {
    if (!selectedForContextmenu?.msg_text) return;
    const el = document.createElement('textarea');
    el.value = selectedForContextmenu?.msg_text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };

  const renderItem = (item, index) => {
    const isMyMessage = item.sender_id === userId;

    const time = moment
      // eslint-disable-next-line no-unsafe-optional-chaining
      .unix(item?.msg_epoch?.seconds ?? item?.msg_epoch * 1000)
      .format('MM.DD.YY h:mm a');

    const isInvalidDate = time === 'Invalid date';
    const isImage =
      item?.attachments?.[0]?.mimeType === 'image/jpeg' ||
      item?.attachments?.[0]?.mimeType === 'image/png';
    const isAudio = item?.attachments?.[0]?.mimeType?.indexOf('audio') > -1;
    const isFile = item?.attachments?.[0]?.mimeType;
    const hasAttachments = isImage || isFile;
    const textWithAttachments = hasAttachments ? '' : 'This message type is not supported yet';
    const hasContextMenu = selectedForContextmenu?.id === item.id;

    const handleContextMenu = (e) => {
      e.preventDefault();
      setAnchorPoint({ x: e.pageX, y: e.pageY });
      setContextMenuIsVisible(true);
      setSelectedForContextmenu(item);
    };

    const createReplyMessage = () => {
      setReplyMessage({
        message_id: item.id,
        sender_id: userId,
        sender_name: username,
        // eslint-disable-next-line no-nested-ternary
        text: isAudio
          ? 'Audio message'
          : // eslint-disable-next-line no-nested-ternary
          isImage
          ? 'Image'
          : isFile
          ? 'File message'
          : item?.msg_text || '',
      });
    };

    const findPos = (obj) => {
      let element = obj;
      let curtop = 0;
      if (element.offsetParent) {
        do {
          curtop += element.offsetTop;
          // eslint-disable-next-line no-cond-assign
        } while ((element = element.offsetParent));
        return curtop;
      }
      return 0;
    };

    const goToId = (message_id) => {
      if (message_id) {
        container.current.scroll(
          0,
          findPos(document.getElementById(item?.reply_message?.message_id)) - 100,
        );
      }
    };

    if (isInvalidDate) return null;

    return (
      <React.Fragment key={index}>
        <MessageContainer
          id={item?.id}
          style={{
            justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
          }}
        >
          <Message
            onContextMenu={handleContextMenu}
            style={{
              background: isMyMessage ? '#E7FDCC' : '#FFFFFF',
            }}
          >
            {item?.reply_message && (
              <ReplyMessage onClick={() => goToId(item.reply_message.message_id)}>
                <ReplyMessageUserName>{item.reply_message?.sender_name}</ReplyMessageUserName>
                <ReplyMessageText>{item.reply_message?.text}</ReplyMessageText>
              </ReplyMessage>
            )}
            {!isMyMessage && <UserName>{item?.sender_name}</UserName>}
            {isImage || isAudio ? (
              <>
                {isImage && (
                  <ImageContainer onClick={() => showGallery(item.attachments[0].path)}>
                    <Image src={item.attachments[0].path} alt="Image path incorrect" />
                  </ImageContainer>
                )}
                {isAudio && <AudioPlayer path={item.attachments[0].path} />}
              </>
            ) : (
              isFile && (
                <ImageContainer>
                  <a download target="_blank" rel="noreferrer" href={item.attachments[0].path}>
                    <Image src="/images/file.png" alt="Image path incorrect" />
                  </a>
                </ImageContainer>
              )
            )}
            <MessageBody>
              {item?.type === 0 && !!item?.msg_text ? item?.msg_text : textWithAttachments}
            </MessageBody>
            <DateWrapper>
              <Date>{time}</Date>
              {isMyMessage && (
                <CheckMarkWrapper>
                  {item?.status === 1 ? (
                    <DoubleCheckMark color="#397EF3" />
                  ) : (
                    <CheckMark color="#397EF3" />
                  )}
                </CheckMarkWrapper>
              )}
            </DateWrapper>
          </Message>
        </MessageContainer>
        {hasContextMenu && (
          <ChatListContextMenu
            anchorPoint={anchorPoint}
            isVisible={contextMenuIsVisible}
            hideMenu={hideMenu}
          >
            {!!item?.msg_text && (
              <ChatListContextMenuItem onClick={() => copyText(item?.msg_text)}>
                <CopyIcon />
                <span>Copy text</span>
              </ChatListContextMenuItem>
            )}
            {isMyMessage && (
              <ChatListContextMenuItem onClick={showDeleteModal}>
                <DeleteIcon />
                <span>Delete</span>
              </ChatListContextMenuItem>
            )}
            <ChatListContextMenuItem onClick={createReplyMessage}>
              <ReplyIcon />
              <span>Reply</span>
            </ChatListContextMenuItem>
          </ChatListContextMenu>
        )}
      </React.Fragment>
    );
  };

  return (
    <Container ref={container}>
      {isClientSupportChat && (
        <SupportChatContainer>
          <SupportLargeIcon />
          <SupportChatTitle>Hello</SupportChatTitle>
          <SupportChatShadow>How can we help you</SupportChatShadow>
        </SupportChatContainer>
      )}
      {isLoading ? <Spinner /> : chatMessages.map(renderItem)}
      <Modal
        title="Delete message"
        visible={deleteConfirmModalIsVisible}
        onOk={deleteMessage}
        onCancel={hideDeleteModal}
        okText="Delete"
      >
        <p>Do you really want to delete the message?</p>
      </Modal>
    </Container>
  );
};

export default MessageListBody;
